import React, { useState, useEffect } from "react";
import { X, ArrowRight, Sparkles, ShieldCheck, Bug, Bell, Receipt, Clock, Calendar, DollarSign, Plus } from "lucide-react";
import "./V1MigrationModal.css";

const V1MigrationModal = ({
  appTitle = "CRM V1.0 Upgrade",
  headline = "Switch to CRM V1.0",
  subheadline = "Faster workflows, intelligent pipeline tracking & instant analytics.",
}) => {
  const isV1 = typeof window !== "undefined" && window.location.hostname === "crm.v1.mawalog.com";
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window === "undefined" || isV1) return false;
    const hasShown = sessionStorage.getItem("crm_v1_modal_shown");
    if (!hasShown) {
      sessionStorage.setItem("crm_v1_modal_shown", "true");
      return true;
    }
    return false;
  });
  const [currentAnim, setCurrentAnim] = useState(1);

  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    window.addEventListener("open-v1-migration-modal", handleOpen);
    return () => window.removeEventListener("open-v1-migration-modal", handleOpen);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentAnim((prev) => (prev % 3) + 1);
    }, 4500);
    return () => clearInterval(timer);
  }, []);

  if (isV1 || !isOpen) return null;

  const handleStayHere = () => {
    setIsOpen(false);
  };

  const handleMoveToV1 = () => {
    setIsOpen(false);
    window.location.href = "https://crm.v1.mawalog.com";
  };

  return (
    <div className="v1-modal-overlay">
      <div className="v1-modal-card">
        <button
          type="button"
          className="v1-modal-close-btn"
          onClick={handleStayHere}
          title="Close"
        >
          <X size={16} />
        </button>

        <div className="v1-modal-header">
          <div className="v1-dashboard-tag">
            <span className="v1-pulse-beacon"></span>
            <span>{appTitle}</span>
          </div>
          <h3 className="v1-modal-title">{headline}</h3>
          <p className="v1-modal-desc">{subheadline}</p>
        </div>

        <div className="v1-compact-stage">
          <div
            className={`v1-anim-pane ${currentAnim === 1 ? "pane-visible" : "pane-hidden"}`}
          >
            <div className="v1-preview-card">
              <div className="crm-pane-top-row">
                <div className="crm-kpi-pill">
                  <span className="crm-kpi-label">Total Leads</span>
                  <span className="crm-kpi-val">144,872</span>
                  <span className="crm-kpi-growth">+104031.58%</span>
                </div>
                <div className="crm-kpi-pill">
                  <span className="crm-kpi-label">New Leads</span>
                  <span className="crm-kpi-val">138,495</span>
                  <span className="crm-kpi-growth">+104031.58%</span>
                </div>
                <div className="crm-kpi-pill">
                  <span className="crm-kpi-label">Converted Leads</span>
                  <span className="crm-kpi-val">138,495</span>
                  <span className="crm-kpi-growth">+104031.58%</span>
                </div>
              </div>

              <div className="crm-analytics-split">
                <div className="crm-funnel-box">
                  <span className="crm-funnel-title">Leads by Stage</span>
                  <div className="crm-funnel-content">
                    <svg viewBox="0 0 100 120" className="crm-funnel-svg">
                      <polygon points="5,5 95,5 82,28 18,28" fill="#DDD6FE" className="crm-funnel-tier crm-funnel-tier-1" />
                      <polygon points="19,32 81,32 70,55 30,55" fill="#C4B5FD" className="crm-funnel-tier crm-funnel-tier-2" />
                      <polygon points="31,59 69,59 60,82 40,82" fill="#A78BFA" className="crm-funnel-tier crm-funnel-tier-3" />
                      <polygon points="41,86 59,86 54,115 46,115" fill="#7C3AED" className="crm-funnel-tier crm-funnel-tier-4" />
                    </svg>

                    <div className="crm-stage-list">
                      <div className="crm-stage-item">
                        <span>
                          <span className="crm-stage-dot" style={{ backgroundColor: "#DDD6FE" }}></span>
                          Pending
                        </span>
                        <b>141,293</b>
                      </div>
                      <div className="crm-stage-item">
                        <span>
                          <span className="crm-stage-dot" style={{ backgroundColor: "#C4B5FD" }}></span>
                          Attempted
                        </span>
                        <b>10</b>
                      </div>
                      <div className="crm-stage-item">
                        <span>
                          <span className="crm-stage-dot" style={{ backgroundColor: "#A78BFA" }}></span>
                          Future
                        </span>
                        <b>4</b>
                      </div>
                      <div className="crm-stage-item">
                        <span>
                          <span className="crm-stage-dot" style={{ backgroundColor: "#7C3AED" }}></span>
                          Contacted
                        </span>
                        <b>377</b>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="crm-chart-box">
                  <div className="crm-chart-head">
                    <span className="crm-chart-title">Leads over time</span>
                    <span className="crm-chart-peak">Peak 139,360</span>
                  </div>

                  <svg viewBox="0 0 240 90" className="crm-line-svg">
                    <defs>
                      <linearGradient id="crmPeakGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#7C3AED" stopOpacity="0.0" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M 10 80 Q 80 80 100 80 Q 120 80 135 15 Q 150 80 170 78 Q 200 80 230 80 L 230 85 L 10 85 Z"
                      fill="url(#crmPeakGrad)"
                      className="crm-animated-area"
                    />
                    <path
                      d="M 10 80 Q 80 80 100 80 Q 120 80 135 15 Q 150 80 170 78 Q 200 80 230 80"
                      fill="none"
                      stroke="#6D28D9"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      className="crm-animated-curve"
                    />
                    <line x1="135" y1="15" x2="135" y2="80" stroke="#C4B5FD" strokeWidth="1" strokeDasharray="2 2" className="crm-peak-guide-line" />
                    <circle cx="135" cy="15" r="3.5" fill="#6D28D9" stroke="#ffffff" strokeWidth="1.5" className="crm-animated-dot" />
                  </svg>

                  <div className="crm-months-axis">
                    <span>Jun</span>
                    <span>Aug</span>
                    <span>Oct</span>
                    <span>Dec</span>
                    <span>Feb</span>
                    <span>Apr</span>
                    <span>Jul</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`v1-anim-pane ${currentAnim === 2 ? "pane-visible" : "pane-hidden"}`}
          >
            <div className="v1-preview-card">
              <div className="crm-rec-head">
                <span className="crm-rec-title">Recurring Invoices</span>
                <button type="button" className="crm-rec-btn-create">
                  <Plus size={11} />
                  <span>Create</span>
                </button>
              </div>

              <div className="crm-rec-kpi-grid">
                <div className="crm-rec-card">
                  <div>
                    <div className="crm-rec-card-lbl">ACTIVE</div>
                    <div className="crm-rec-card-num">12</div>
                  </div>
                  <div className="crm-rec-icon-badge" style={{ background: "#ECFDF5", color: "#10B981" }}>
                    <Receipt size={12} />
                  </div>
                </div>
                <div className="crm-rec-card">
                  <div>
                    <div className="crm-rec-card-lbl">MRV</div>
                    <div className="crm-rec-card-num">₹55.63Cr</div>
                  </div>
                  <div className="crm-rec-icon-badge" style={{ background: "#EFF6FF", color: "#3B82F6" }}>
                    <DollarSign size={12} />
                  </div>
                </div>
                <div className="crm-rec-card">
                  <div>
                    <div className="crm-rec-card-lbl">PENDING</div>
                    <div className="crm-rec-card-num">7</div>
                  </div>
                  <div className="crm-rec-icon-badge" style={{ background: "#FEF3C7", color: "#F59E0B" }}>
                    <Clock size={12} />
                  </div>
                </div>
                <div className="crm-rec-card">
                  <div>
                    <div className="crm-rec-card-lbl">COMPLETED</div>
                    <div className="crm-rec-card-num">1</div>
                  </div>
                  <div className="crm-rec-icon-badge" style={{ background: "#F1F5F9", color: "#64748B" }}>
                    <Calendar size={12} />
                  </div>
                </div>
              </div>

              <div className="crm-rec-table-wrap">
                <div className="crm-rec-th">
                  <span style={{ width: "22%" }}>PROFILE</span>
                  <span style={{ width: "28%" }}>CUSTOMER</span>
                  <span style={{ width: "20%" }}>FREQUENCY</span>
                  <span style={{ width: "16%" }}>AMOUNT</span>
                  <span style={{ width: "14%", textAlign: "right" }}>STATUS</span>
                </div>

                <div className="crm-rec-tbody">
                  <div className="crm-rec-row crm-rec-row-1">
                    <span style={{ width: "22%" }} className="crm-rec-code">REC-0012</span>
                    <span style={{ width: "28%" }} className="crm-rec-name">Tata Consultancy</span>
                    <span style={{ width: "20%" }} className="crm-rec-freq">Monthly</span>
                    <span style={{ width: "16%" }} className="crm-rec-amt">₹14.25Cr</span>
                    <span style={{ width: "14%", textAlign: "right" }}>
                      <span className="crm-status-pill active">Active</span>
                    </span>
                  </div>
                  <div className="crm-rec-row crm-rec-row-2">
                    <span style={{ width: "22%" }} className="crm-rec-code">REC-0011</span>
                    <span style={{ width: "28%" }} className="crm-rec-name">Reliance Retail</span>
                    <span style={{ width: "20%" }} className="crm-rec-freq">Quarterly</span>
                    <span style={{ width: "16%" }} className="crm-rec-amt">₹28.40Cr</span>
                    <span style={{ width: "14%", textAlign: "right" }}>
                      <span className="crm-status-pill active">Active</span>
                    </span>
                  </div>
                  <div className="crm-rec-row crm-rec-row-3">
                    <span style={{ width: "22%" }} className="crm-rec-code">REC-0010</span>
                    <span style={{ width: "28%" }} className="crm-rec-name">Infosys BPM</span>
                    <span style={{ width: "20%" }} className="crm-rec-freq">Monthly</span>
                    <span style={{ width: "16%" }} className="crm-rec-amt">₹8.65Cr</span>
                    <span style={{ width: "14%", textAlign: "right" }}>
                      <span className="crm-status-pill pending">Pending</span>
                    </span>
                  </div>
                  <div className="crm-rec-row crm-rec-row-4">
                    <span style={{ width: "22%" }} className="crm-rec-code">REC-0009</span>
                    <span style={{ width: "28%" }} className="crm-rec-name">HCL Technologies</span>
                    <span style={{ width: "20%" }} className="crm-rec-freq">Annual</span>
                    <span style={{ width: "16%" }} className="crm-rec-amt">₹4.33Cr</span>
                    <span style={{ width: "14%", textAlign: "right" }}>
                      <span className="crm-status-pill active">Active</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`v1-anim-pane ${currentAnim === 3 ? "pane-visible" : "pane-hidden"}`}
          >
            <div className="v1-preview-card">
              <div className="crm-forecast-tabs">
                <span className="crm-tab-pill">Leads Hub</span>
                <span className="crm-tab-pill">Revenue</span>
                <span className="crm-tab-pill">Pipeline</span>
                <span className="crm-tab-pill active">Forecast</span>
                <span className="crm-tab-pill">Next Actions</span>
              </div>

              <div className="crm-fc-kpis">
                <div className="crm-fc-card">
                  <div className="crm-fc-lbl">Leads</div>
                  <div className="crm-fc-val">590</div>
                  <div className="crm-fc-growth">▲ 8%</div>
                </div>
                <div className="crm-fc-card">
                  <div className="crm-fc-lbl">Opportunities</div>
                  <div className="crm-fc-val">11</div>
                  <div className="crm-fc-growth">▲ 8.3%</div>
                </div>
                <div className="crm-fc-card">
                  <div className="crm-fc-lbl">Accounts</div>
                  <div className="crm-fc-val">127</div>
                  <div className="crm-fc-growth">▲ 126.4%</div>
                </div>
                <div className="crm-fc-card">
                  <div className="crm-fc-lbl">Quoted Value</div>
                  <div className="crm-fc-val">₹13.95Cr</div>
                  <div className="crm-fc-growth">▲ 11.3%</div>
                </div>
              </div>

              <div className="crm-fc-chart-box">
                <div className="d-flex align-items-center justify-content-between">
                  <span style={{ fontSize: "9px", fontWeight: "700", color: "#1e293b" }}>
                    Leads — 12 months actual, 3 projected
                  </span>
                  <div className="crm-fc-legend">
                    <span>
                      <span style={{ display: "inline-block", width: "8px", height: "3px", backgroundColor: "#6366F1", marginRight: "3px" }}></span>
                      Actual
                    </span>
                    <span>
                      <span style={{ display: "inline-block", width: "8px", height: "3px", borderTop: "2px dashed #A5B4FC", marginRight: "3px" }}></span>
                      Projected
                    </span>
                  </div>
                </div>

                <svg viewBox="0 0 250 65" style={{ width: "100%", height: "70px", overflow: "visible" }}>
                  <defs>
                    <linearGradient id="fcGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366F1" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#6366F1" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M 5 55 Q 35 50 65 42 Q 95 44 125 55 Q 155 54 185 10 L 185 60 L 5 60 Z"
                    fill="url(#fcGrad)"
                    className="crm-animated-area"
                  />
                  <path
                    d="M 5 55 Q 35 50 65 42 Q 95 44 125 55 Q 155 54 185 10"
                    fill="none"
                    stroke="#4F46E5"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    className="crm-fc-actual-path"
                  />
                  <line x1="185" y1="10" x2="185" y2="60" stroke="#C7D2FE" strokeWidth="1" strokeDasharray="2 2" className="crm-fc-milestone-line" />
                  <circle cx="185" cy="10" r="3" fill="#4F46E5" stroke="#ffffff" strokeWidth="1.5" className="crm-animated-dot crm-fc-dot-1" />
                  <path
                    d="M 185 10 Q 200 45 215 48 Q 230 45 245 42"
                    fill="none"
                    stroke="#818CF8"
                    strokeWidth="2"
                    strokeDasharray="4 3"
                    strokeLinecap="round"
                    className="crm-fc-proj-path"
                  />
                  <circle cx="215" cy="48" r="2.5" fill="#818CF8" stroke="#ffffff" strokeWidth="1" className="crm-animated-dot crm-fc-dot-2" />
                  <circle cx="245" cy="42" r="2.5" fill="#818CF8" stroke="#ffffff" strokeWidth="1" className="crm-animated-dot crm-fc-dot-3" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="v1-highlights-grid">
          <div className="v1-highlight-chip">
            <Sparkles size={13} className="v1-hl-icon purple" />
            <span>Enhancements & Updates</span>
          </div>
          <div className="v1-highlight-chip">
            <ShieldCheck size={13} className="v1-hl-icon blue" />
            <span>Enhanced Securities</span>
          </div>
          <div className="v1-highlight-chip">
            <Bug size={13} className="v1-hl-icon green" />
            <span>Critical Bug Fixes</span>
          </div>
          <div className="v1-highlight-chip">
            <Bell size={13} className="v1-hl-icon amber" />
            <span>Instant Notifications</span>
          </div>
        </div>

        <div className="v1-modal-actions">
          <button
            type="button"
            className="v1-btn-stay"
            onClick={handleStayHere}
          >
            Stay Here
          </button>
          <button
            type="button"
            className="v1-btn-move"
            onClick={handleMoveToV1}
          >
            Move to V1.0
            <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default V1MigrationModal;
